import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import './DeineZahlungsdaten.css';
import InfoboxSchutz from '../components/InfoboxSchutz';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { useAppContext } from '../context/AppContext';
import { applicationService } from '../backend/applicationService';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import * as yup from 'yup';
import { electronicFormatIBAN, isValidIBAN } from 'ibantools';
import { useYupValidation } from '../hooks/useYupValidation';
import { de } from 'date-fns/locale';
import GlobalErrorAlert from '../components/GlobalErrorAlert';
import { useGlobalError } from '../context/GlobalErrorContext';

const validationRules = yup.object().shape({
  kontoinhaber: yup.string().required('Bitte Kontoinhaber*in eingeben'),
  iban: yup
    .string()
    .required('Bitte IBAN eingeben')
    .transform(value => electronicFormatIBAN(value))
    .test('iban-validation', 'IBAN ist ungültig', value => (value ? isValidIBAN(value) : false)),
  sepaMandatAccepted: yup
    .boolean()
    .required('Bitte akzeptiere das SEPA-Mandat')
    .oneOf([true], 'Bitte akzeptiere das SEPA-Mandat'),
});

function DeineZahlungsdaten() {
  const navigate = useNavigate();
  const theme = useTheme();
  //const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const { state, dispatch } = useAppContext();

  const [formData, setFormData] = useState({
    kontoinhaber: `${state.formData.vorname} ${state.formData.nachname}`,
    iban: '',
    sepaMandatAccepted: false,
    ...state.formData,
  });

  const { showGlobalError, clearGlobalError } = useGlobalError();
  const { errors, fieldRefs, validateField, validateForm } = useYupValidation(validationRules);
  const [, setLoading] = useState(false);
  const applicationVersionRef = useRef(1);

  useEffect(() => {
    if (state.editMode && state.qualifiedApplication?.payment) {
      applicationVersionRef.current = state.qualifiedApplication.version;
      setFormData(prev => ({
        ...prev,
        kontoinhaber: state.qualifiedApplication.payment.data.accountOwner,
        iban: formatIBAN(state.qualifiedApplication.payment.data.iban),
      }));
    }
  }, [state]);

  const formatIBAN = value => {
    // Nur Buchstaben und Zahlen
    const cleanValue = value.replace(/[^A-Z0-9]/gi, '').toUpperCase();

    // In 4er Gruppen aufteilen
    return cleanValue.replace(/(.{4})/g, '$1 ').trim();
  };

  const handleInputChange = async event => {
    const { name, value, type, checked } = event.target;

    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (name === 'iban') {
      const formattedValue = formatIBAN(value);
      setFormData(prev => ({ ...prev, [name]: formattedValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleBlur = async event => {
    const { name, value, type, checked } = event.target;
    await validateField(name, type === 'checkbox' ? checked : value);
  };

  const handleSubmit = async event => {
    event.preventDefault();
    clearGlobalError();

    if (!(await validateForm(formData))) {
      return;
    }

    setLoading(true);

    // Correlation ID generieren
    const correlationId = `corr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Zahlungsdaten vorbereiten
    const paymentData = {
      ...formData,
      iban: formData.iban.replace(/\s/g, ''), // Leerzeichen entfernen für Backend
      correlationId,
    };

    const applicationObject = state.applicationObject;

    applicationObject.version = applicationVersionRef.current + 1;

    applicationObject.payment = {
      type: 'debit',
      data: {
        iban: paymentData.iban,
        accountOwner: formData.kontoinhaber,
      },
    };
    applicationObject.acceptedTermsAndConditions = true;
    applicationObject.status = 'NEW';

    const response = await applicationService.prepareQualifiedApplication(
      state.applicationId,
      applicationObject,
      state.sessionId,
    );
    if (response.success) {
      dispatch({
        type: 'SET_FORM_DATA',
        payload: { ...state.formData, ...paymentData },
      });

      dispatch({
        type: 'SET_QUALIFIED_APPLICATION',
        payload: {
          qualifiedApplication: response.data,
        },
      });
      dispatch({ type: 'SET_CURRENT_STEP', payload: 5 });
      navigate('/zusammenfassung');
    } else {
      showGlobalError('Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
    }

    setLoading(false);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
      <Box className="wrapper" role="main" aria-label="Angabe der Zahlungsdaten">
        <Container className="main-content-scroll-container">
          <Box className="main-content-with-sidebar">
            {/* --- PAGE CONTENT  --- */}
            <Box className="page-content">
              <Typography variant="h1" className="headline-large-bold">
                Deine Zahlungsdaten
              </Typography>
              <p style={{ marginTop: '16px' }}> Mit * gekennzeichnete Felder sind Pflichtfelder. </p>
              <GlobalErrorAlert sx={{ mt: 1 }} />
              <Box>
                <Box variant="body2" component="p" sx={{ mt: 3, mb: 2 }} textAlign="justify">
                  {state.formData.fromMail && 'Willkommen zurück! '}Bitte gib deine Kontoverbindung an.
                </Box>
              </Box>
              <Box component="form" role="form" onSubmit={handleSubmit} noValidate>
                <Grid container spacing={3} className="form-wrapper">
                  <Grid item xs={12} lg={8}>
                    <Grid container spacing={3} className="form-content-container">
                      <Box className="form-input-flex">
                        <Grid item xs={12} md={6} style={{ flex: 1 }}>
                          <Typography
                            variant="h2"
                            className="headline-medium-bold"
                            id="jumpmark-maincontent"
                            tabIndex={-1}
                            sx={{ mt: 2, mb: 1, outline: 'none' }}
                          >
                            Kontoinhaber*in
                          </Typography>
                          <TextField
                            id="kontoinhaber"
                            inputRef={fieldRefs.kontoinhaber}
                            variant="filled"
                            fullWidth
                            name="kontoinhaber"
                            label="Kontoinhaber*in"
                            value={formData.kontoinhaber}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            required
                            error={!!errors.kontoinhaber}
                            helperText={errors.kontoinhaber}
                            className="input-field larger-input-text"
                            autoComplete="off"
                            slotProps={{
                              formHelperText: { 'data-testid': 'kontoinhaber-error' },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} style={{ flex: 1 }}>
                          <Typography variant="h2" className="headline-medium-bold" sx={{ mt: 2, mb: 1 }}>
                            IBAN
                          </Typography>
                          <TextField
                            inputRef={fieldRefs.iban}
                            variant="filled"
                            fullWidth
                            id="iban"
                            name="iban"
                            label="IBAN"
                            value={formData.iban}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            required
                            error={!!errors.iban}
                            helperText={errors.iban}
                            className="input-field larger-input-text"
                            slotProps={{
                              input: {
                                autoComplete: 'off',
                                style: {
                                  fontFamily: 'monospace',
                                  letterSpacing: '0.1em',
                                },
                              },
                              formHelperText: { 'data-testid': 'iban-error' },
                            }}
                          />
                        </Grid>
                      </Box>

                      <Box mt={2} mb={1}>
                        <Grid item xs={12} md={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="sepaMandatAccepted"
                                checked={formData.sepaMandatAccepted}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                sx={{
                                  color: 'var(--color-primary-text)',
                                  '&.Mui-checked': {
                                    color: 'var(--color-primary-text)',
                                  },
                                }}
                                slotProps={{
                                  input: {
                                    ref: fieldRefs.sepaMandatAccepted,
                                    'aria-invalid': !!errors.sepaMandatAccepted,
                                    'aria-describedby': errors.sepaMandatAccepted ? 'sepa-error-message' : null,
                                  },
                                }}
                              />
                            }
                            label={
                              <Typography variant="body2" component="span" className="h2-style">
                                Ja, ich erteile das SEPA-Lastschriftmandat.
                              </Typography>
                            }
                          />

                          {errors.sepaMandatAccepted && (
                            <Typography
                              id="sepa-error-message"
                              variant="caption"
                              color="var(--color-error)"
                              display="block"
                              sx={{ mt: 1 }}
                              role="alert"
                              aria-live="assertive"
                              aria-atomic="true"
                            >
                              {errors.sepaMandatAccepted}
                            </Typography>
                          )}
                        </Grid>
                      </Box>

                      <Box>
                        <Typography variant="body2" color="var(--color-primary-text)" component="p" sx={{ mb: 2 }}>
                          Ich ermächtige die assona GmbH (Gläubiger-ID: DE-89BER00000108559), Zahlungen gemäß der
                          vereinbarten Zahlweise von meinem Konto mittels Lastschrift einzuziehen. Zugleich weise ich
                          mein Kreditinstitut an, die von der assona GmbH auf meinem Konto gezogenen Lastschriften
                          einzulösen. Die Mandatsreferenznummer entspricht hierbei der Vertragsnummer.
                        </Typography>

                        <Typography variant="body2" color="var(--color-primary-text)" component="p" sx={{ mb: 2 }}>
                          Hinweis: Du kannst innerhalb von acht Wochen, beginnend mit dem Belastungsdatum, die
                          Erstattung des belasteten Betrags verlangen. Es gelten dabei die mit deinem Kreditinstitut
                          vereinbarten Bedingungen.
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Sticky InfoboxSchutz für Desktop */}
                  {isDesktop && (
                    <div className="infobox-sidebar-container">
                      <InfoboxSchutz />
                    </div>
                  )}
                </Grid>

                {/* Navigation Button */}
                <Box className="button-container" sx={{ mt: 4 }}>
                  <Button
                    className="button-general button-action"
                    variant="contained"
                    type="submit"
                    aria-label="Zusammenfassung anzeigen"
                  >
                    Zusammenfassung anzeigen
                  </Button>
                </Box>
              </Box>{' '}
              {/* Ende form */}
            </Box>{' '}
            {/* className="page-content" */}
            {/* --- ENDE PAGE CONTENT  --- */}
          </Box>{' '}
          {/* className="main-content-with-sidebar" */}
        </Container>{' '}
        {/* className="main-content-scroll-container" */}
      </Box>{' '}
      {/* className="wrapper" */}
    </LocalizationProvider>
  );
}

export default DeineZahlungsdaten;
